/**
 * StewardWorkspace — the right-side sheet the planning happens in.
 *
 * There is no Sheet primitive in this codebase, so the Radix Dialog is styled as
 * a full-height right sheet (precedent-compatible with BookTripSheet, which also
 * dresses the Dialog). Premium register: quiet motion, `prefers-reduced-motion`
 * respected throughout, skeleton/status while the turn thinks, no toy animation.
 *
 * THE THREAD RESUMES. Every turn has always been written to steward_messages;
 * until now nothing read them back, so the traveler watched the conversation
 * disappear on every reload while Steward carried on referring to it. The sheet
 * now loads the thread on open and only falls back to the opener when there is
 * genuinely nothing to resume.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Send, X, Loader2, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { stewardApi } from "@/lib/api";
import { useStewardTurn } from "@/hooks/useStewardTurn";
import type { StewardMessage, StewardScope } from "@/lib/types";
import StewardCard from "./StewardCard";
import StewardDayPlanCard from "./StewardDayPlanCard";
import StewardThinking from "./StewardThinking";
import { scopeHeadline } from "./stewardCard-utils";
import { messageBlocks } from "./stewardDay-utils";

/**
 * "Your outbound moved to Sep 9." — shown when the trip changed under the
 * conversation, so a correction never arrives as an unexplained change of mind.
 */
function ChangeNotice({ lines }: { lines: string[] }) {
  if (lines.length === 0) return null;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
      <RefreshCw className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" aria-hidden="true" />
      <div className="min-w-0 space-y-0.5">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">
          Since you were last here
        </p>
        {lines.map((l) => (
          <p key={l} className="text-xs leading-snug text-amber-900">
            {l}
          </p>
        ))}
      </div>
    </div>
  );
}

/** A first message the sheet should send the moment it opens. */
export interface StewardSeed {
  message: string;
  scope?: StewardScope | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tripId: string;
  tripName: string;
  /** Cached opener — seeds the thread so re-opening is instant. */
  opener?: string | null;
  openerChips?: string[];
  conversationId?: string | null;
  /** Optional first message (a tapped chip, or an add-slot request). */
  seed?: StewardSeed | null;
  onSeedConsumed?: () => void;
}

export default function StewardWorkspace({
  open,
  onOpenChange,
  tripId,
  tripName,
  opener,
  openerChips,
  conversationId,
  seed,
  onSeedConsumed,
}: Props) {
  const { messages, streaming, status, send, seedOpener, hydrate, hydrated } =
    useStewardTurn(tripId, conversationId);
  const [draft, setDraft] = useState("");
  const threadRef = useRef<HTMLDivElement | null>(null);
  const sentSeed = useRef(false);

  /*
   * The chips array is a fresh literal on every TripDetail render, so depending
   * on it directly re-ran the opener effect forever. Freeze it on content.
   */
  const chipsKey = JSON.stringify(openerChips ?? []);
  const chips = useMemo(() => JSON.parse(chipsKey) as string[], [chipsKey]);

  /*
   * The persisted thread. Fetched only while the sheet is open (a closed sheet
   * has no thread to show) and never cached stale — a turn taken in another tab
   * should be here when this one opens.
   */
  const {
    data: history,
    isLoading: historyLoading,
    isFetched: historyFetched,
  } = useQuery({
    queryKey: ["stewardConversation", tripId],
    queryFn: () => stewardApi.conversation(tripId),
    enabled: open && !!tripId,
    staleTime: 0,
    retry: false,
  });

  /*
   * Keyed on isFetched, NOT on data: a failed history read must still resolve
   * hydration. Otherwise one broken read takes out a second, unrelated feature
   * — the opener never seeds and the sheet opens permanently blank.
   */
  useEffect(() => {
    if (!open || !historyFetched) return;
    hydrate(history?.messages ?? []);
  }, [open, historyFetched, history, hydrate]);

  /*
   * The opener is the EMPTY-THREAD greeting, so it must wait for hydration to
   * report back. Seeding it first would stack "You have 3 unplanned days…" on
   * top of a conversation that already worked those days out.
   */
  useEffect(() => {
    if (!open || !hydrated || !opener) return;
    seedOpener(opener, chips);
  }, [open, hydrated, opener, chips, seedOpener]);

  /*
   * A chip tapped from the banner, or an add slot, arrives as a seed — send once.
   *
   * ⚠️ THE `hydrated` GATE IS LOAD-BEARING, AND ITS ABSENCE SHIPPED A BUG.
   * send() pushes the user turn straight into `messages`, and hydrate() refuses
   * to overwrite a non-empty thread (correctly — a late history response must
   * never clobber a live turn). So without this gate the seed won and the
   * ENTIRE history was silently discarded. The opener effect above was gated
   * and this one was not, which made it look fine: opening from the gap banner
   * showed history, opening from "Add to your trip" showed none. That second
   * path is the one travelers actually use. Fixing a race on one entry point is
   * not fixing the race.
   */
  useEffect(() => {
    if (!open || !hydrated || !seed || sentSeed.current) return;
    sentSeed.current = true;
    void send(seed.message, seed.scope);
    onSeedConsumed?.();
  }, [open, hydrated, seed, send, onSeedConsumed]);

  useEffect(() => {
    if (!open) sentSeed.current = false;
  }, [open]);

  // Keep the newest turn in view as it streams.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, status]);

  const submit = (text: string) => {
    const t = text.trim();
    if (!t || streaming) return;
    setDraft("");
    void send(t);
  };

  const resumed = (history?.messages.length ?? 0) > 0;
  const scopeLine = seed?.scope ? scopeHeadline(seed.scope) : null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none" />
        <DialogPrimitive.Content
          className={cn(
            "fixed right-0 top-0 z-50 flex h-full w-full max-w-[560px] flex-col border-l border-border bg-background shadow-2xl",
            "duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
            "motion-reduce:animate-none motion-reduce:duration-0",
          )}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
            <div className="min-w-0">
              <DialogPrimitive.Title className="text-base font-semibold tracking-tight">
                Your Steward
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="truncate text-xs text-muted-foreground">
                {scopeLine ? `${tripName} · ${scopeLine}` : tripName}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground motion-reduce:transition-none"
              aria-label="Close Steward"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>

          {/* Thread */}
          <div ref={threadRef} className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
            {historyLoading && messages.length === 0 && (
              <div className="space-y-2" aria-label="Loading your conversation">
                <div className="h-3 w-2/3 animate-pulse rounded bg-muted motion-reduce:animate-none" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-muted motion-reduce:animate-none" />
              </div>
            )}

            {!historyLoading && messages.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Ask me anything about this trip — where to stay, a car for the drive
                south, what to do with the open days, how to time it against your
                flight home.
              </p>
            )}

            {/* A quiet marker so a resumed thread reads as continuous rather than
                as a wall of text that appeared from nowhere. */}
            {resumed && messages.length > 0 && (
              <div className="flex items-center gap-3" aria-hidden="true">
                <span className="h-px flex-1 bg-border" />
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Earlier
                </span>
                <span className="h-px flex-1 bg-border" />
              </div>
            )}

            {/* What moved while they were away — above the thread, because it
                reframes everything below it. Suppressed once a live turn has
                reported its own changes, so the same news isn't told twice. */}
            {!streaming && messages.length > 0 && (
              <ChangeNotice lines={history?.changes ?? []} />
            )}

            {messages.map((m, i) => (
              <Turn key={i} message={m} tripId={tripId} onChip={submit} />
            ))}

            {streaming && <StewardThinking status={status} />}
          </div>

          {/* Composer */}
          <form
            className="flex items-end gap-2 border-t border-border px-5 py-4"
            onSubmit={(e) => {
              e.preventDefault();
              submit(draft);
            }}
          >
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit(draft);
                }
              }}
              rows={1}
              placeholder="Ask Steward…"
              aria-label="Message Steward"
              className="max-h-32 min-h-[38px] flex-1 resize-none rounded-lg border border-border bg-card px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:border-[hsl(var(--primary))]/50"
            />
            <button
              type="submit"
              disabled={streaming || draft.trim().length === 0}
              aria-label="Send"
              className="inline-flex h-[38px] items-center justify-center rounded-lg bg-[hsl(var(--foreground))] px-3 text-white transition-opacity hover:opacity-90 disabled:opacity-40 motion-reduce:transition-none"
            >
              {streaming ? (
                <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** One turn: prose, then any cards it proposed, then its follow-up chips. */
function Turn({
  message,
  tripId,
  onChip,
}: {
  message: StewardMessage;
  tripId: string;
  onChip: (text: string) => void;
}) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <p className="max-w-[85%] rounded-2xl rounded-br-sm bg-[hsl(var(--primary))]/10 px-3.5 py-2 text-sm text-foreground">
          {message.content}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Steward noticed something moved before she answered THIS turn. */}
      {message.changes && message.changes.length > 0 && (
        <ChangeNotice lines={message.changes} />
      )}

      {message.pending && !message.content ? (
        // The live indicator below the thread carries the wait; a second
        // skeleton here would double the noise for one pending turn.
        null
      ) : (
        message.content
          .split(/\n{2,}/)
          .filter((p) => p.trim().length > 0)
          .map((para, i) => (
            <p key={i} className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
              {para}
            </p>
          ))
      )}

      {/* Cards and dictated days interleave in the order the turn emitted them,
          so "here's your day, and here's a hotel for the night at the end of it"
          reads in that order rather than being split into two blocks. */}
      {messageBlocks(message).map((block) =>
        block.kind === "day" ? (
          <StewardDayPlanCard key={`day-${block.key}`} day={block.day} tripId={tripId} />
        ) : (
          <StewardCard key={`card-${block.key}`} card={block.card} tripId={tripId} />
        ),
      )}

      {message.error && <p className="text-xs text-destructive">{message.error}</p>}

      {message.chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {message.chips.map((chip) => (
            <button
              key={chip}
              type="button"
              onClick={() => onChip(chip)}
              className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-[hsl(var(--primary))]/40 hover:text-foreground motion-reduce:transition-none"
            >
              {chip}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
